import type { Events } from '../../core/TimeData';
import type { VoyageGroup } from '../../core/Timelines';
import type { ScenarioLocation } from '../../../lib/planningData';
import type { ClientId } from '../../core/ClientId';
import { convertToDefaultCurrency } from '../../../state/zustand';
import { type MarcuraPortCosts } from '../../marcura/context';
import type { Currency } from '@seaber/common/graphql/types';

export type PortCostByVcMaps = Map<
  string,
  {
    cost: number;
    currency: string;
    estimatedCost?: number;
  }
>;

export function getIntegrationPortCosts(
  voyageGroups: VoyageGroup<Events>[],
  portCostByVcMaps: PortCostByVcMaps,
  locationById: Map<number, ScenarioLocation>,
  portCostMarcuraMaps: MarcuraPortCosts,
  vesselWeight: number | null | undefined,
  baseCurrency: Currency,
) {
  const costs = new Map<ClientId | null, { value: number; currency: string }>();
  for (const { orders, portCalls } of voyageGroups) {
    for (const pc of portCalls) {
      const pcId = pc.id;
      const locationId = pc.locationId;
      const location = locationById.get(locationId);
      if (!location) continue;
      for (const order of orders) {
        const vc = order.incomingVoyageCharterRelation;
        const assortmentId = order.assortmentByAssortmentId?.id;
        if (portCostMarcuraMaps && assortmentId && vesselWeight) {
          const portCosts = portCostMarcuraMaps.get(locationId, assortmentId);
          if (portCosts?.data.costs.length) {
            const portCost = portCosts.data.costs.find(
              (c) => vesselWeight >= c.dwtMin && vesselWeight <= c.dwtMax,
            );
            if (
              portCost &&
              (portCost.costLoading || portCost.costDischarging)
            ) {
              const value =
                convertToDefaultCurrency(
                  Math.max(
                    portCost.costLoading || 0,
                    portCost.costDischarging || 0,
                  ),
                  baseCurrency,
                ) ?? 0;

              // Prioritize the highest value
              if (
                (!!value && !costs.has(pcId)) ||
                (costs.get(pcId)?.value || 0) < value
              ) {
                costs.set(pcId, {
                  value,
                  currency: baseCurrency,
                });
              }
            }
          }
        }
        if (vc && !costs.has(pcId)) {
          const vcId = vc.voyageCharterId;
          const portCost = portCostByVcMaps.get(`${vcId}-${locationId}`);
          if (!portCost) continue;
          const value =
            convertToDefaultCurrency(
              portCost.cost ?? portCost.estimatedCost ?? 0,
              portCost.currency,
            ) ?? 0;

          costs.set(pcId, { value, currency: portCost.currency });
        }
      }
    }
  }
  return costs;
}
